/* eslint-disable global-require */
import React, {
  createContext,
  ReactNode,
  useReducer,
  useContext,
  useEffect,
} from 'react';
import {
  HopeGlobalContext,
  SET_CROSS_PATIENT_HIS as SET_CROSS_PATIENT_HIS_GLOBAL,
} from '@mummssoftware/global-context';
import JSZip from 'jszip';
import { useIntl } from 'react-intl';
import { Context } from '../../context';
import {
  SET_ALERT_INFO,
  SET_INDIVIDUAL_PATIENT_HIS,
  SET_INDIVIDUAL_PATIENT_HEADER,
  CLOSE_HIS_MODAL,
  OPEN_HIS_MODAL,
  SET_OPEN_ROW,
  SET_GRID_API,
  SET_USER_CAN_APPROVE,
  APR,
  DWN,
  ACCPT,
  NoHISToDownload,
} from './constants';
import {
  updateGridRow,
  getReadyForDownload,
  zipHISFiles,
  formatApiError,
  getPostData,
  formatModalTitle,
  findApprovalRoles,
} from './his-utils';

type HISContextProviderProps = {
  children: ReactNode;
};

type initFunction = () => undefined;

const initFunc = () => undefined;

type HISContext = {
  alertInfo: mumms.ErrorInfo | null;
  individualPatientHeader: any | null;
  individualPatientHIS: any | null;
  openRow: any | null;
  modalTitle: string | null;
  modalOpen: boolean;
  dispatch: any | null;
  fetchHIS: any | null;
  gridAPI: any | null;
  clearAlert: (() => void) | initFunction;
  fetchPatientApprovalHIS: ((row: any) => void) | initFunction;
  approvePatientHIS: (() => void) | initFunction;
  DownloadUnsentXML: (() => void) | initFunction;
  revertToDownload: ((event_id: string) => void) | initFunction;
  acceptPatientHIS: ((row: any) => void) | initFunction;
  userCanApprove: boolean;
};

type HISContextState = {
  alertInfo: mumms.ErrorInfo | null;
  individualPatientHeader: any | null;
  individualPatientHIS: any | null;
  openRow: any | null;
  modalTitle: string | null;
  modalOpen: boolean;
  dispatch: any | null;
  fetchHIS: any | null;
  gridAPI: any | null;
  clearAlert: (() => void) | initFunction;
  fetchPatientApprovalHIS: ((row: any) => void) | initFunction;
  approvePatientHIS: (() => void) | initFunction;
  DownloadUnsentXML: (() => void) | initFunction;
  revertToDownload: ((event_id: string) => void) | initFunction;
  acceptPatientHIS: ((row: any) => void) | initFunction;
  userCanApprove: boolean;
};

const initialState = {
  alertInfo: null,
  individualPatientHeader: null,
  individualPatientHIS: null,
  openRow: null,
  modalTitle: null,
  modalOpen: false,
  dispatch: null,
  fetchHIS: null,
  gridAPI: null,
  clearAlert: initFunc,
  fetchPatientApprovalHIS: initFunc,
  approvePatientHIS: initFunc,
  DownloadUnsentXML: initFunc,
  revertToDownload: initFunc,
  acceptPatientHIS: initFunc,
  userCanApprove: false,
};

const initHISContext = {
  alertInfo: null,
  individualPatientHeader: null,
  individualPatientHIS: null,
  openRow: null,
  modalTitle: null,
  modalOpen: false,
  dispatch: null,
  fetchHIS: null,
  gridAPI: null,
  clearAlert: initFunc,
  fetchPatientApprovalHIS: initFunc,
  approvePatientHIS: initFunc,
  DownloadUnsentXML: initFunc,
  revertToDownload: initFunc,
  acceptPatientHIS: initFunc,
  userCanApprove: false,
};

export const HISContextReducer = (
  state: HISContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_ALERT_INFO:
      newState.alertInfo = action.payload;
      break;
    case SET_INDIVIDUAL_PATIENT_HIS:
      newState.individualPatientHIS = action.payload;
      break;
    case SET_INDIVIDUAL_PATIENT_HEADER:
      newState.individualPatientHeader = action.payload;
      break;
    case CLOSE_HIS_MODAL:
      newState.individualPatientHeader = null;
      newState.individualPatientHIS = null;
      newState.modalTitle = null;
      newState.modalOpen = false;
      break;
    case OPEN_HIS_MODAL:
      newState.modalOpen = true;
      newState.modalTitle = action.payload;
      break;
    case SET_OPEN_ROW:
      newState.openRow = action.payload;
      break;
    case SET_GRID_API:
      newState.gridAPI = action.payload;
      break;
    case SET_USER_CAN_APPROVE:
      newState.userCanApprove = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const HISContext = createContext<HISContext>(
  initHISContext as HISContext,
);
export const HISContextProvider = ({ children }: HISContextProviderProps) => {
  const [
    {
      alertInfo,
      individualPatientHeader,
      individualPatientHIS,
      openRow,
      modalTitle,
      modalOpen,
      fetchHIS,
      gridAPI,
      userCanApprove,
    },
    dispatch,
  ] = useReducer(HISContextReducer, initialState);
  const {
    agency,
    workflow,
    pim,
    siteId,
    currentUser,
    currentUser: { siteId: userSiteId, username, userId },
  } = useContext(Context);
  const { dispatch: globalDispatch, globalCrossPatientHIS } = useContext(
    HopeGlobalContext,
  );
  const { personNumber } = currentUser;
  const { formatMessage } = useIntl();
  // Gets 'ready for download' rows from ui and fetches xml for each.
  // then creates zip folder and adds each xml to folder before zipping.
  const DownloadUnsentXML = async () => {
    if (!globalCrossPatientHIS) return;
    const readyForDownload = getReadyForDownload(globalCrossPatientHIS);
    if (!readyForDownload.length) {
      dispatch({ type: SET_ALERT_INFO, payload: NoHISToDownload });
      return;
    }
    const xmlResponse: any = await workflow.postBatchHISData(
      siteId || userSiteId.toString(),
      readyForDownload,
      agency,
      username,
      userId.toString(),
    );
    const zip = new JSZip();
    const HISZipFolder = zip.folder('hospice_data');

    if (xmlResponse instanceof Response) {
      dispatch({
        type: SET_ALERT_INFO,
        payload: formatApiError(xmlResponse, formatMessage, 'workflow'),
      });
    } else if (xmlResponse.length && HISZipFolder) {
      zipHISFiles(
        xmlResponse,
        HISZipFolder,
        getCrossPatientHIS,
        readyForDownload,
      );
    }
  };

  // add selected row to 'ready for download'
  // does not delete the underlying download dto
  const revertToDownload = (rowData: any) => {
    const { data } = rowData;
    updateGridRow(
      { hisadminstatusshortname: DWN, ...data },
      data,
      formatMessage,
      DWN,
    );
    const rowNodes = [gridAPI.getDisplayedRowAtIndex(rowData.rowIndex)];
    gridAPI.redrawRows({ rowNodes });
  };

  const approvePatientHIS = async () => {
    const dataPost = getPostData(openRow, personNumber, APR);

    const approveResponse = await workflow.postHISData(
      dataPost,
      agency,
      'POST',
    );

    if (approveResponse instanceof Response) {
      dispatch({
        type: SET_ALERT_INFO,
        payload: formatApiError(approveResponse, formatMessage, 'workflow'),
      });
    } else {
      updateGridRow(approveResponse, openRow, formatMessage, DWN);
      dispatch({
        type: CLOSE_HIS_MODAL,
        payload: null,
      });
    }
  };

  const acceptPatientHIS = async (rowData: any) => {
    const dataPost = getPostData(rowData.data, personNumber, ACCPT);

    const acceptanceResponse = await workflow.postHISData(
      dataPost,
      agency,
      'POST',
    );
    if (acceptanceResponse instanceof Response) {
      dispatch({
        type: SET_ALERT_INFO,
        payload: formatApiError(acceptanceResponse, formatMessage, 'workflow'),
      });
    } else {
      updateGridRow(acceptanceResponse, rowData.data, formatMessage, '');
      const rowNodes = [gridAPI.getDisplayedRowAtIndex(rowData.rowIndex)];
      gridAPI.redrawRows({ rowNodes });
    }
  };

  // Initial api call to fetch rows for all patients
  const getCrossPatientHIS = async () => {
    const allHISResponse: any = await workflow.getCrossPatientHIS(
      agency,
      siteId || userSiteId.toString(),
    );
    if (allHISResponse instanceof Response) {
      dispatch({
        type: SET_ALERT_INFO,
        payload: formatApiError(allHISResponse, formatMessage, 'workflow'),
      });
    } else if (allHISResponse[0]?.type !== null) {
      // agency is turned on and there are rows to show
      globalDispatch &&
        globalDispatch({
          type: SET_CROSS_PATIENT_HIS_GLOBAL,
          payload: allHISResponse.filter((his: any) => his.siteid === siteId),
        });
    } else {
      // agency isn't enabled
      globalDispatch &&
        globalDispatch({
          type: SET_CROSS_PATIENT_HIS_GLOBAL,
          payload: [],
        });
    }
  };

  // api call to fetch individual HIS data for approval
  const fetchPatientApprovalHIS = async (rowData: any) => {
    const { patientnumber, type, episodenum } = rowData;
    dispatch({ type: OPEN_HIS_MODAL, payload: formatModalTitle(rowData) });

    const patientHeader = await pim.getPatientHeader(patientnumber, agency);

    if (patientHeader instanceof Response) {
      dispatch({
        type: SET_ALERT_INFO,
        payload: formatApiError(patientHeader, formatMessage, 'pim'),
      });
    } else {
      const HospiceInfo = await workflow.getPatientHIS(
        patientnumber,
        episodenum,
        agency,
      );
      if (HospiceInfo instanceof Response) {
        dispatch({
          type: SET_ALERT_INFO,
          payload: formatApiError(HospiceInfo, formatMessage, 'workflow'),
        });
      } else {
        dispatch({
          type: SET_INDIVIDUAL_PATIENT_HEADER,
          payload: patientHeader,
        });
        dispatch({
          type: SET_INDIVIDUAL_PATIENT_HIS,
          payload: { ...HospiceInfo, type },
        });
      }
    }
  };

  const clearAlert = () => {
    dispatch({
      type: SET_ALERT_INFO,
      payload: null,
    });
    dispatch({
      type: CLOSE_HIS_MODAL,
      payload: null,
    });
  };

  // init fetch data
  useEffect(() => {
    if (currentUser) {
      dispatch({
        type: SET_USER_CAN_APPROVE,
        payload: findApprovalRoles(currentUser),
      });
    }
    if (workflow && !globalCrossPatientHIS) {
      getCrossPatientHIS();
    }
  }, []);

  return (
    <HISContext.Provider
      value={{
        alertInfo,
        individualPatientHeader,
        individualPatientHIS,
        openRow,
        modalTitle,
        modalOpen,
        fetchHIS,
        gridAPI,
        dispatch,
        clearAlert,
        fetchPatientApprovalHIS,
        approvePatientHIS,
        DownloadUnsentXML,
        revertToDownload,
        acceptPatientHIS,
        userCanApprove,
      }}
    >
      {children}
    </HISContext.Provider>
  );
};

export default HISContextProvider;
